import { useState, useRef, useEffect, useCallback } from "react";
import type { ListingResponse } from "../features/listings/types";

// ── window 타입 (ListingsPage와 동일)
declare global {
  interface Window { kakao: any; __kakaoMapLoading?: boolean; }
}

const KAKAO_KEY = import.meta.env.VITE_KAKAO_MAP_KEY;

// ── loadKakaoSDK: ListingsPage 코드 100% 동일 복사 ──────────
function loadKakaoSDK(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.kakao?.maps?.LatLng) { resolve(true); return; }
    if (!KAKAO_KEY) { resolve(false); return; }
    if (window.__kakaoMapLoading) {
      const id = setInterval(() => {
        if (window.kakao?.maps?.LatLng) { clearInterval(id); resolve(true); }
      }, 200);
      return;
    }
    window.__kakaoMapLoading = true;
    const s = document.createElement("script");
    s.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_KEY}&autoload=false`;
    s.onload = () => window.kakao.maps.load(() => { window.__kakaoMapLoading = false; resolve(true); });
    s.onerror = () => { window.__kakaoMapLoading = false; resolve(false); };
    document.head.appendChild(s);
  });
}

// ── 매물 상태별 색상 (statusColorMode 전용)
const STATUS_COLORS: Record<string, string> = {
  "active": "#FBBF24",   // 골드 — 공개중
  "sold":   "#3B82F6",   // 파랑 — 거래완료
  "hidden": "#6B7280",   // 회색 — 숨김
};

// ── 매물 유형별 색상 (ListingsPage와 동일)
const TYPE_COLORS: Record<string, string> = {
  "\uB18D\uC9C0": "#16A34A",
  "\uC784\uC57C": "#92400E",
  "\uBB18\uD130": "#7C3AED",
  "\uACF5\uC7A5": "#6B7280",
  "\uCC3D\uACE0": "#EA580C",
  "\uC0C1\uAC00": "#3B82F6",
  "\uC8FC\uD0DD": "#2563EB",
};
const DEFAULT_TYPE_COLOR = "#6B7280";

// ── 가격 요약 포맷 (ListingsPage와 동일)
function shortPrice(manwon: number): string {
  if (manwon < 10000) {
    return manwon.toLocaleString() + "\uB9CC";
  }
  const eok = manwon / 10000;
  if (eok < 100) {
    return (eok % 1 === 0 ? eok.toFixed(0) : eok.toFixed(1)) + "\uC5B5";
  }
  return eok.toFixed(0) + "\uC5B5";
}

// ── Props
interface KakaoMapViewProps {
  /** false면 null 반환 (언마운트) → true가 되면 div 새로 마운트 → 80ms 딜레이 → 지도 초기화 */
  active: boolean;
  /** 이 값이 바뀔 때 마커 업데이트. "viewMode|filteredKey" 등 */
  updateKey: string;
  /** 지도 div의 CSS height */
  height: string | number;
  /** 가격말풍선 모드: 매물 목록 */
  listings?: ListingResponse[];
  /** 클릭 이벤트 위임용 (가격말풍선 클릭 → 인포오버레이) */
  allListings?: ListingResponse[];
  /** 골드dot 모드: 좌표 목록 */
  coords?: { lat: number; lng: number }[];
  /** drag/zoom 활성화 여부 (기본 true) */
  interactive?: boolean;
  /** 지도 위에 표시할 안내 텍스트 (optional) */
  blindNotice?: React.ReactNode;
  /** 지도 위 투명 오버레이 클릭 핸들러 (논인터랙티브 모드용) */
  onAreaClick?: () => void;
  /** 지도 div 추가 스타일 */
  mapStyle?: React.CSSProperties;
  /**
   * 마커 색상을 property_type 대신 status 기반으로 표시
   * active=#FBBF24(골드), sold=#3B82F6(파랑), hidden=#6B7280(회색)
   */
  statusColorMode?: boolean;
  /** 상세보기 클릭 시 호출 (id 전달). 미설정 시 새 탭으로 이동 */
  onDetailClick?: (id: string) => void;
  /**
   * CSS filter string for map dark effect.
   * e.g. "brightness(0.3) contrast(1.2) saturate(0) hue-rotate(180deg)"
   * filter 모드에서는 마커를 filter 밖 별도 레이어에 React로 렌더링.
   */
  filterStyle?: string;
  /** filter 모드 고정 중심 위도 (기본 36.5) */
  centerLat?: number;
  /** filter 모드 고정 중심 경도 (기본 127.5) */
  centerLng?: number;
  /** filter 모드 고정 줌 레벨 (기본 13) */
  mapLevel?: number;
}

export function KakaoMapView({
  active,
  updateKey,
  height,
  listings,
  allListings,
  coords,
  interactive = true,
  blindNotice,
  onAreaClick,
  mapStyle,
  filterStyle,
  centerLat,
  centerLng,
  mapLevel,
  statusColorMode = false,
  onDetailClick,
}: KakaoMapViewProps) {
  const mapRef          = useRef<any>(null);
  const overlaysRef     = useRef<any[]>([]);
  const infoOverlayRef  = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInitRef      = useRef(false);
  const [pinPositions, setPinPositions] = useState<{ x: number; y: number }[]>([]);

  // active가 false → ref 초기화
  // (다음번에 active=true가 될 때 fresh map을 만들어야 하므로)
  useEffect(() => {
    if (!active) {
      mapRef.current       = null;
      mapInitRef.current   = false;
      overlaysRef.current  = [];
      infoOverlayRef.current = null;
      setPinPositions([]);
    }
  }, [active]);

  // ── 가격말풍선 모드: infoOverlay 열기 (ListingsPage openInfoOverlay 100% 동일)
  function openInfoOverlay(kakao: any, listing: ListingResponse, pos: any) {
    if (infoOverlayRef.current) { infoOverlayRef.current.setMap(null); }

    const bg = TYPE_COLORS[listing.property_type] || DEFAULT_TYPE_COLOR;
    const cardHtml =
      `<div style="position:relative;background:#fff;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.15);padding:16px 18px 14px;min-width:200px;max-width:260px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">` +
      `<button data-lt-close="1" style="position:absolute;top:8px;right:10px;background:none;border:none;font-size:18px;color:#999;cursor:pointer;padding:0;line-height:1;">\u00D7</button>` +
      `<div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">` +
        `<span style="display:inline-block;padding:2px 8px;border-radius:10px;background:${bg};color:#fff;font-size:11px;font-weight:700;">${listing.property_type}</span>` +
        `<span style="font-size:12px;color:#888;">${listing.region_sido} ${listing.region_sigungu}</span>` +
      `</div>` +
      `<div style="font-size:20px;font-weight:800;color:#1a1a1a;margin-bottom:2px;">${shortPrice(listing.price_manwon)}</div>` +
      `<div style="font-size:13px;color:#666;margin-bottom:10px;">${listing.area_pyeong.toLocaleString()}\uD3C9 \u00B7 ${listing.region_eupmyeondong || ""}</div>` +
      `<button data-lt-detail="${listing.id}" style="display:block;width:100%;text-align:center;padding:8px 0;border:none;border-radius:8px;background:linear-gradient(135deg,#B8972E,#D4AF37);color:#fff;font-size:13px;font-weight:700;cursor:pointer;">\uC0C1\uC138\uBCF4\uAE30 \u2192</button>` +
      `<div style="position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid #fff;"></div>` +
    `</div>`;

    const infoOverlay = new kakao.maps.CustomOverlay({
      position: pos,
      content: cardHtml,
      yAnchor: 1.15,
      zIndex: 10,
    });
    infoOverlay.setMap(mapRef.current);
    infoOverlayRef.current = infoOverlay;
  }

  // ── 가격말풍선 모드: updateMarkers (ListingsPage updateMarkers 100% 동일)
  const updateMarkers = useCallback(async (listingsArg: ListingResponse[]) => {
    const ok = await loadKakaoSDK();
    if (!ok || !mapContainerRef.current) return;
    const kakao = window.kakao;

    if (!mapInitRef.current) {
      const center = new kakao.maps.LatLng(36.5, 127.5);
      mapRef.current = new kakao.maps.Map(mapContainerRef.current, { center, level: 13 });
      mapInitRef.current = true;
    }

    // 기존 오버레이 정리
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];
    if (infoOverlayRef.current) { infoOverlayRef.current.setMap(null); infoOverlayRef.current = null; }

    const bounds = new kakao.maps.LatLngBounds();
    const allOverlays: any[] = [];

    listingsArg.forEach((listing) => {
      if (!listing.blind_lat || !listing.blind_lng) return;
      const pos = new kakao.maps.LatLng(listing.blind_lat, listing.blind_lng);
      bounds.extend(pos);

      const bg = statusColorMode
        ? (STATUS_COLORS[(listing as any).status] || DEFAULT_TYPE_COLOR)
        : (TYPE_COLORS[listing.property_type] || DEFAULT_TYPE_COLOR);
      const price = shortPrice(listing.price_manwon);

      const markerHtml =
        `<div style="cursor:pointer;position:relative;" data-lt-marker="${listing.id}">` +
        `<div style="background:${bg};color:#fff;font-size:12px;font-weight:700;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:4px 8px;border-radius:6px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.25);text-align:center;">${price}</div>` +
        `<div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid ${bg};margin:0 auto;"></div>` +
        `</div>`;

      const overlay = new kakao.maps.CustomOverlay({
        position: pos,
        content: markerHtml,
        yAnchor: 1.3,
        zIndex: 3,
        map: mapRef.current,
      });
      allOverlays.push(overlay);
    });

    overlaysRef.current = allOverlays;

    if (allOverlays.length > 0) {
      mapRef.current.relayout();
      mapRef.current.setBounds(bounds);
      // 최대 줌 레벨 11 이하로 제한 (북한 노출 방지)
      setTimeout(() => {
        if (mapRef.current && mapRef.current.getLevel() > 11) {
          mapRef.current.setLevel(11);
        }
      }, 60);
    }
  }, []);

  // ── 골드dot 모드: initMapWithDots
  const initMapWithDots = useCallback(async (
    coordsArg: { lat: number; lng: number }[],
    opts: {
      filterStyle?: string;
      centerLat?: number;
      centerLng?: number;
      mapLevel?: number;
      interactive?: boolean;
    },
  ) => {
    const ok = await loadKakaoSDK();
    if (!ok || !mapContainerRef.current) return;
    const kakao = window.kakao;

    if (!mapInitRef.current) {
      const clat = opts.centerLat ?? 36.5;
      const clng = opts.centerLng ?? 127.5;
      const level = opts.mapLevel ?? 13;
      const center = new kakao.maps.LatLng(clat, clng);
      mapRef.current = new kakao.maps.Map(mapContainerRef.current, { center, level });
      if (opts.interactive === false) {
        mapRef.current.setDraggable(false);
        mapRef.current.setZoomable(false);
      }
      mapInitRef.current = true;
    }

    if (coordsArg.length === 0) return;

    if (opts.filterStyle) {
      // ── filter 모드: 픽셀 좌표 계산 → React 핀 레이어로 렌더링 (CSS filter 밖)
      setTimeout(() => {
        if (!mapRef.current || !mapContainerRef.current) return;
        const proj = mapRef.current.getProjection();
        const w = mapContainerRef.current.offsetWidth;
        const h = mapContainerRef.current.offsetHeight;
        if (w === 0 || h === 0) return;

        const centerLatLng = mapRef.current.getCenter();
        const centerWorld  = proj.pointFromCoords(centerLatLng);

        const positions = coordsArg
          .map((c) => {
            const world = proj.pointFromCoords(new kakao.maps.LatLng(c.lat, c.lng));
            return {
              x: w / 2 + (world.x - centerWorld.x),
              y: h / 2 + (world.y - centerWorld.y),
            };
          })
          .filter((p) => p.x > -5 && p.x < w + 5 && p.y > -5 && p.y < h + 5);

        setPinPositions(positions);
      }, 200);
    } else {
      // ── standard 모드: CustomOverlay 골드 핀 + setBounds
      const pinHtml =
        '<div style="width:16px;height:20px;cursor:pointer;">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20">' +
        '<path d="M8 0C4.69 0 2 2.69 2 6c0 4.5 6 14 6 14s6-9.5 6-14c0-3.31-2.69-6-6-6z" fill="#FBBF24" stroke="#B8922E" stroke-width="0.8"/>' +
        '<circle cx="8" cy="6" r="2.2" fill="rgba(0,0,0,0.25)"/>' +
        '</svg>' +
        '</div>';

      const bounds = new kakao.maps.LatLngBounds();
      coordsArg.forEach((c) => {
        const pos = new kakao.maps.LatLng(c.lat, c.lng);
        bounds.extend(pos);
        new kakao.maps.CustomOverlay({
          position: pos,
          content: pinHtml,
          yAnchor: 1.0,
          zIndex: 500,
          map: mapRef.current,
        });
      });
      mapRef.current.setBounds(bounds);
    }
  }, []);

  // ── 메인 effect: ListingsPage 패턴 100% 동일
  // active=true + updateKey 변경 → setTimeout 80ms → 지도 초기화/업데이트
  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => {
        if (listings) {
          updateMarkers(listings);
        } else if (coords) {
          initMapWithDots(coords, { filterStyle, centerLat, centerLng, mapLevel, interactive });
        }
      }, 80);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, updateKey]);

  // ── 가격말풍선 클릭 이벤트 위임 (ListingsPage window 이벤트 위임 100% 동일)
  useEffect(() => {
    if (!listings) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const closeBtn = target.closest("[data-lt-close]") as HTMLElement | null;
      if (closeBtn && infoOverlayRef.current) {
        infoOverlayRef.current.setMap(null);
        infoOverlayRef.current = null;
        return;
      }
      const detailBtn = target.closest("[data-lt-detail]") as HTMLElement | null;
      if (detailBtn) {
        const id = detailBtn.getAttribute("data-lt-detail");
        if (id) {
          if (onDetailClick) {
            onDetailClick(id);
          } else {
            window.open(`/listings/${id}`, "_blank");
          }
        }
        return;
      }
      const markerEl = target.closest("[data-lt-marker]") as HTMLElement | null;
      if (markerEl) {
        const id = markerEl.getAttribute("data-lt-marker");
        if (id) {
          if (onDetailClick) {
            // onDetailClick 제공 시 → 팝업 없이 바로 패널 오픈
            onDetailClick(id);
          } else {
            const src = allListings ?? listings ?? [];
            const listing = src.find((l) => l.id === id);
            if (listing && listing.blind_lat && listing.blind_lng && window.kakao?.maps) {
              const pos = new window.kakao.maps.LatLng(listing.blind_lat, listing.blind_lng);
              openInfoOverlay(window.kakao, listing, pos);
            }
          }
        }
      }
    }
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allListings, listings, onDetailClick]);

  // ── active=false → null 반환 (언마운트 → 다음번 active=true 시 fresh mount)
  if (!active) return null;

  const hasFilter = !!filterStyle;

  return (
    <div style={{ position: "relative" }}>
      {/* 안내 문구 (optional) */}
      {blindNotice && (
        <div style={{
          padding: "8px 16px",
          background: "var(--bg-card)",
          border: "1px solid var(--border-light)",
          borderBottom: "none",
          borderRadius: "var(--radius-xl) var(--radius-xl) 0 0",
          fontSize: 13, color: "var(--text-muted)", textAlign: "center",
        }}>
          {blindNotice}
        </div>
      )}

      {/* 지도 div — filter 모드면 래퍼 div 안에, 아니면 직접 */}
      {hasFilter ? (
        <div style={{ filter: filterStyle }}>
          <div
            ref={mapContainerRef}
            style={{
              width: "100%",
              height,
              display: "block",
              overflow: "hidden",
              ...mapStyle,
            }}
          />
        </div>
      ) : (
        <div
          ref={mapContainerRef}
          style={{
            width: "100%",
            height,
            display: "block",
            borderRadius: blindNotice
              ? "0 0 var(--radius-xl) var(--radius-xl)"
              : undefined,
            border: blindNotice
              ? "1px solid var(--border-light)"
              : undefined,
            borderTop: blindNotice ? "none" : undefined,
            overflow: "hidden",
            ...mapStyle,
          }}
        />
      )}

      {/* 골드 핀 레이어 (filter 모드 전용 — CSS filter 밖에서 렌더링) */}
      {hasFilter && pinPositions.length > 0 && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}>
          {pinPositions.map((p, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: p.x,
                top: p.y,
                transform: "translate(-50%, -100%)",
                lineHeight: 0,
                filter: "drop-shadow(0 0 5px rgba(244,63,94,0.85))",
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 16 20">
                <path d="M8 0C4.69 0 2 2.69 2 6c0 4.5 6 14 6 14s6-9.5 6-14c0-3.31-2.69-6-6-6z" fill="#F43F5E" stroke="#BE123C" strokeWidth="0.8"/>
                <circle cx="8" cy="6" r="2.2" fill="rgba(0,0,0,0.3)"/>
              </svg>
            </div>
          ))}
        </div>
      )}

      {/* 논인터랙티브 모드: 클릭 투명 오버레이 */}
      {onAreaClick && (
        <div
          style={{ position: "absolute", inset: 0, cursor: "pointer", zIndex: 10 }}
          onClick={onAreaClick}
        />
      )}

    </div>
  );
}
